import React, { FC, useEffect, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useParams } from 'react-router-dom'
import {
  fetchTaskLearningScopeSetting,
  updateTaskLearningScopeSetting,
} from '../../../../states/containers/task/taskSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import { RequestPatchTaskLearningScopeSetting, TaskLearningScopeMember } from '../../../../states/types/task'
import MemberListModule from '../../../components/common/MemberListModule'
import SettingModal from '../../../components/common/SettingModal'
import { RootState } from '../../../../states/containers/store'

const STATUS_LIST = [
  {
    label: "個人",
    value: "member",
  },
  {
    label: "チーム",
    value: "team",
  },
  {
    label: "会社",
    value: "company",
  },
]

const SELECT_LIST = [
  {
    label: "会社全体の設定に準拠する",
    value: "common",
  },
  {
    label: "個人に設定する",
    value: "member",
  },
  {
    label: "チームに設定する",
    value: "team",
  },
  {
    label: "会社に設定する",
    value: "company",
  },
]

const Index: FC = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [learningScope, setLearningScope] = useState<string>("company")

  const [originMemberList, setOriginMemberList] = useState<TaskLearningScopeMember[]>([])
  const [commonValue, setCommonValue] = useState<string>("company")

  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false)
  const [targetMembers, setTargetMembers] = useState<TaskLearningScopeMember[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const openUpdateModal = (memberIds: number[]) => {
    setTargetMembers(originMemberList.filter((v) => memberIds.includes(v.id)))
    setIsDisplayModal(true)
  }

  const closeModal = () => {
    setTargetMembers([])
    setIsDisplayModal(false)
  }

  const changeLearningScope = (data: {commonFlg: boolean, memberIds?: number[], type: string}) => {
    const param: RequestPatchTaskLearningScopeSetting = { change_common_setting: data.commonFlg }
    const learning_scope = data.type
    if (data.commonFlg) {
      param.learning_scope = learning_scope ?? "company"
    } else {
      param.member_ids = data.memberIds
      param.use_common_setting = learning_scope === "common"
      if (data.type !== "common") {
        param.learning_scope = learning_scope ?? "company"
      }
    }
    dispatch(updateTaskLearningScopeSetting(
      param,
      () => {
        closeModal()
        if (data.commonFlg) {
          setCommonValue(learning_scope ?? "company")
          alert.success("会社全体の設定を変更しました")
        } else {
          setOriginMemberList((cur) => cur.map((v) => (
            param.member_ids?.includes(v.id)
              ? { ...v, use_common_setting: param.use_common_setting || false, learning_scope: param.learning_scope ?? "company" }
              : v
          )))
          alert.success("ユーザーの設定を変更しました")
        }
      },
    ))
  }

  useEffect(() => {
    setIsLoading(true)
    dispatch(fetchTaskLearningScopeSetting(
      (result) => {
        setOriginMemberList(result.members)
        setCommonValue(result.common.learning_scope)
        setLearningScope(result.common.learning_scope)
        setIsLoading(false)
      },
    ))
  }, [])

  return (
    <section className="task_page">
      <Helmet>
        <title>
          {
            location.pathname === `/${language}${URL.SETTING_TASK_LEARNING_SCOPE_SETTING.path}` ? (
              `Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - 仕分け提案に使用する過去データの範囲設定`
            ) : (
              `Qasee - 工数管理 - 仕分け提案に使用する過去データの範囲設定`
            )
          }
        </title>
      </Helmet>
      <section className="breadcrumb">
        {
          location.pathname === `/${language}${URL.SETTING_TASK_LEARNING_SCOPE_SETTING.path}` ? (
            <>
              設定
              &nbsp;&gt;&nbsp;
              <Link
                to={setPath(URL.SETTING_COMMON.path, language)}
              >
                {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
              </Link>
            </>
          ) : (
            <>
              工数管理
            </>
          )
        }
        &nbsp;&gt;&nbsp;
        仕分け提案に使用する過去データの範囲設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          仕分け提案に使用する過去データの範囲設定
        </h1>
      </section>
      <section className="page_description">
        <p style={{ marginBottom: "4px" }}>作業データの仕分け提案に使用する過去データの範囲を選択できます。</p>
        <p style={{ marginBottom: "4px" }}>範囲の選択によって、他のユーザーの仕分け履歴を参考にするか、自分の履歴のみを使うかを調整できます。</p>
      </section>
      {
        isLoading ? (
          <LoadingSquare>
            <>
              設定を読み込んでいます…
            </>
          </LoadingSquare>
        ) : (
          <>
            {
              login.role_target_restriction || (
                <section className="page_contents content-area">
                  <div className="content-block">
                    <div className="block-header" style={{ display: "block" }}>
                      <div className="title" style={{ marginBottom: "16px" }}>
                        会社全体の設定
                      </div>
                    </div>
                    <div
                      className="block-detail"
                      style={{
                        border: "solid 1px #CCC",
                        borderRadius: "4px",
                        padding: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          marginBottom: "16px",
                        }}
                      >
                        学習する範囲を
                        <select
                          onChange={(e) => setLearningScope(e.target.value)}
                          value={learningScope ?? "null"}
                          defaultValue={learningScope ?? "null"}
                          style={{
                            border: "solid 1px #999",
                            backgroundColor: "#FFF",
                            borderRadius: "3px",
                            padding: "6px 8px",
                            appearance: "listbox",
                            WebkitAppearance: "listbox",
                          }}
                        >
                          {
                            STATUS_LIST.map((v, i) => (
                              <option value={v.value} key={`option_${i + 1}`}>
                                { v.label }
                              </option>
                            ))
                          }
                        </select>
                        に設定します (初期設定: 会社)
                      </div>
                      <button
                        type="button"
                        className="button"
                        onClick={() => changeLearningScope({ commonFlg: true, type: learningScope })}
                      >
                        設定する
                      </button>
                    </div>
                  </div>
                </section>
              )
            }
            <MemberListModule
              originMemberList={originMemberList.map((v) => ({ ...v, value: v.learning_scope }))}
              statusList={STATUS_LIST}
              commonValue={commonValue}
              valueLabel="仕分け提案に使用する過去データの範囲設定"
              type="string"
              modalOpenCallback={(memberIds) => openUpdateModal(memberIds)}
            />
            <SettingModal
              targetMemberList={targetMembers.map((v) => ({ ...v, value: v.learning_scope }))}
              selectList={SELECT_LIST}
              label="仕分け提案に使用する過去データの範囲設定"
              isDisplayModal={isDisplayModal}
              submitCallback={(data: { commonFlg: boolean; memberIds: number[]; type: string }) => changeLearningScope(data)}
              closeCallback={closeModal}
            />
          </>
        )
      }
    </section>
  )
}

export default Index
